import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  type RowSelectionState,
  type OnChangeFn,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Skeleton } from "@/components/ui/skeleton"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  isLoading?: boolean
  onRowClick?: (row: TData) => void
  emptyMessage?: string
  sorting?: SortingState
  onSortingChange?: OnChangeFn<SortingState>
  columnVisibility?: VisibilityState
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>
  rowSelection?: RowSelectionState
  onRowSelectionChange?: OnChangeFn<RowSelectionState>
  getRowId?: (row: TData) => string
}

export function DataTable<TData, TValue>({
  columns,
  data,
  isLoading,
  onRowClick,
  emptyMessage = "Brak wynikow",
  sorting,
  onSortingChange,
  columnVisibility,
  onColumnVisibilityChange,
  rowSelection,
  onRowSelectionChange,
  getRowId,
}: DataTableProps<TData, TValue>) {
  const stateObj: Record<string, unknown> = {}
  const optionsObj: Record<string, unknown> = {}

  if (sorting !== undefined && onSortingChange) {
    stateObj.sorting = sorting
    optionsObj.onSortingChange = onSortingChange
    optionsObj.manualSorting = true
  }

  if (columnVisibility !== undefined) {
    stateObj.columnVisibility = columnVisibility
    if (onColumnVisibilityChange) {
      optionsObj.onColumnVisibilityChange = onColumnVisibilityChange
    }
  }

  if (rowSelection !== undefined && onRowSelectionChange) {
    stateObj.rowSelection = rowSelection
    optionsObj.onRowSelectionChange = onRowSelectionChange
    optionsObj.enableRowSelection = true
  }

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(Object.keys(stateObj).length > 0 ? { state: stateObj } : {}),
    ...optionsObj,
    ...(getRowId ? { getRowId } : {}),
  })

  if (isLoading) {
    const visibleCols = columns.filter((col) => {
      const colId = (col as { accessorKey?: string; id?: string }).accessorKey ??
        (col as { id?: string }).id ?? ""
      if (columnVisibility && colId && columnVisibility[colId] === false) return false
      return true
    })
    return (
      <div className="surface-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {visibleCols.map((col, i) => (
                <TableHead key={i}>
                  {typeof col.header === "string" ? col.header : ""}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                {visibleCols.map((_, j) => (
                  <TableCell key={j}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )
  }

  return (
    <div className="surface-card overflow-hidden">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const sortDir = header.column.getIsSorted()
                return (
                  <TableHead
                    key={header.id}
                    aria-sort={sortDir === "asc" ? "ascending" : sortDir === "desc" ? "descending" : header.column.getCanSort() ? "none" : undefined}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className={onRowClick ? "cursor-pointer" : ""}
                onClick={() => onRowClick?.(row.original)}
                onKeyDown={onRowClick ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    onRowClick(row.original)
                  }
                } : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                data-state={row.getIsSelected() ? "selected" : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="text-sm">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center text-sm text-muted-foreground">
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
